import { useEffect, useMemo, useState } from 'react';
import styles from './LabellingPicker.module.css';
import type { Algorithm } from '../types';
import { RmaiApiKeyMissingError, listAlgorithms } from '../rmai';

interface Props {
  onSelect: (algorithm: Algorithm) => void;
  onClose: () => void;
}

/**
 * Modal that lists every algorithm the labelling tool can label for.
 * Grouped by anatomical region derived from the algorithm's `view`,
 * filterable via a search box. Fetches the list from RMAI on mount.
 *
 * Renders a distinct "configure your API key" state when RMAI rejects
 * the request for missing credentials — the reader can then paste a
 * key into a text field right in the modal (localStorage-backed).
 */
export default function LabellingPicker({ onSelect, onClose }: Props) {
  const [algorithms, setAlgorithms] = useState<Algorithm[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [apiKeyMissing, setApiKeyMissing] = useState(false);
  const [query, setQuery] = useState('');
  const [apiKeyInput, setApiKeyInput] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const list = await listAlgorithms();
        if (!cancelled) setAlgorithms(list);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof RmaiApiKeyMissingError) {
          setApiKeyMissing(true);
        } else {
          setError(err instanceof Error ? err.message : String(err));
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const saveKey = () => {
    const clean = apiKeyInput.trim();
    if (!clean) return;
    localStorage.setItem('rmai-api-key', clean);
    setApiKeyMissing(false);
    setApiKeyInput('');
    // Trigger a refresh
    setAlgorithms(null);
    (async () => {
      try {
        const list = await listAlgorithms();
        setAlgorithms(list);
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
  };

  const grouped = useMemo(() => bucketByRegion(algorithms ?? [], query), [algorithms, query]);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <header className={styles.header}>
          <h2 className={styles.title}>Label for benchmark</h2>
          <button className={styles.close} onClick={onClose} title="Close">
            ×
          </button>
        </header>

        {apiKeyMissing ? (
          <div className={styles.apiKeyPrompt}>
            <p>
              RMAI API key not configured. Paste your key below and press{' '}
              <kbd>Enter</kbd> to save.
            </p>
            <input
              autoFocus
              className={styles.apiKeyInput}
              type="password"
              placeholder="X-RMAI-API-Key value"
              value={apiKeyInput}
              onChange={(e) => setApiKeyInput(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') saveKey();
              }}
            />
            <div className={styles.actions}>
              <button className={styles.btn} onClick={saveKey}>
                Save
              </button>
            </div>
            <p className={styles.hint}>
              This is stored in your browser (<code>localStorage['rmai-api-key']</code>)
              and never sent anywhere but the RMAI backend.
            </p>
          </div>
        ) : error ? (
          <div className={styles.error}>{error}</div>
        ) : algorithms === null ? (
          <div className={styles.loading}>Loading algorithms…</div>
        ) : (
          <>
            <div className={styles.searchWrap}>
              <input
                autoFocus
                className={styles.search}
                type="search"
                placeholder="Search algorithms (name, region, or key)"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className={styles.groups}>
              {grouped.length === 0 ? (
                <div className={styles.empty}>No algorithms match “{query}”.</div>
              ) : (
                grouped.map(({ region, items }) => (
                  <section key={region} className={styles.group}>
                    <h3 className={styles.groupTitle}>{region}</h3>
                    <div className={styles.grid}>
                      {items.map((a) => (
                        <button
                          key={a.algorithm}
                          className={styles.card}
                          onClick={() => onSelect(a)}
                          title={a.description ?? undefined}
                        >
                          <div className={styles.cardName}>{a.name}</div>
                          <div className={styles.cardMeta}>
                            {a.algorithm} · {a.unit} · {a.requiredLandmarks.length} landmarks
                          </div>
                        </button>
                      ))}
                    </div>
                  </section>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/**
 * Bucket algorithms into anatomical-region groups based on the `view`
 * field. Views like AP_PA_FULL_SPINE / LATERAL_LUMBAR etc. get mapped
 * to human-readable region names. Applied AFTER the search filter so
 * empty regions don't render.
 */
function bucketByRegion(
  algorithms: Algorithm[],
  query: string,
): { region: string; items: Algorithm[] }[] {
  const filtered = filter(algorithms, query);
  const order = ['Cervical spine', 'Skull / craniovertebral', 'Thoracic spine', 'Lumbar spine', 'Pelvis / hip', 'Shoulder', 'Other'];
  const byRegion = new Map<string, Algorithm[]>();
  for (const a of filtered) {
    const region = regionOf(a);
    const list = byRegion.get(region) ?? [];
    list.push(a);
    byRegion.set(region, list);
  }
  const result: { region: string; items: Algorithm[] }[] = [];
  for (const region of order) {
    const items = byRegion.get(region);
    if (items && items.length > 0) {
      // Sort within region by tool id — matches the RMAI catalog order.
      items.sort((a, b) => a.toolId - b.toolId);
      result.push({ region, items });
    }
  }
  return result;
}

function regionOf(a: Algorithm): string {
  const v = a.view;
  if (v.startsWith('LATERAL_SKULL') || v.startsWith('AP_SKULL')) return 'Skull / craniovertebral';
  if (v.startsWith('LATERAL_CERVICAL') || v.startsWith('AP_CERVICAL') || v.includes('OPEN_MOUTH')) return 'Cervical spine';
  if (v.includes('THORACIC')) return 'Thoracic spine';
  if (v.includes('LUMBAR')) return 'Lumbar spine';
  if (v.includes('PELVIS') || v.includes('HIP')) return 'Pelvis / hip';
  if (v.includes('SHOULDER')) return 'Shoulder';
  if (v.startsWith('AP_PA_FULL_SPINE')) return 'Thoracic spine'; // Cobb — canonical spine measurement
  return 'Other';
}

function filter(algorithms: Algorithm[], query: string): Algorithm[] {
  const q = query.trim().toLowerCase();
  if (!q) return algorithms;
  return algorithms.filter((a) => {
    return (
      a.name.toLowerCase().includes(q) ||
      a.algorithm.toLowerCase().includes(q) ||
      regionOf(a).toLowerCase().includes(q) ||
      String(a.toolId).includes(q)
    );
  });
}
